import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, HostListener, input, output } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { LoaderDots } from '../loader-dots';

import { Icon, IconType } from 'app/shared/icon';

export type TooltipPositions = 'after' | 'before' | 'above' | 'below' | 'left' | 'right';

@Component({
    selector: 'stby-button-icon',
    imports: [Icon, RouterLink, NgClass, NgTemplateOutlet, LoaderDots, MatTooltip, TranslatePipe],
    templateUrl: './button-icon.html',
    styleUrl: './button-icon.scss',
})
export class ButtonIcon {
    /** Button props */
    type = input<'default' | 'filled' | 'filled-primary' | 'outline'>('default');
    disabled = input<boolean>(false);
    loading = input<boolean>(false);
    id = input<string>('');
    class = input<string>('');
    size = input<'large' | 'medium' | 'small'>('large');
    tooltip = input.required<string>();
    tooltipPosition = input<TooltipPositions>('below');
    /** Overrides the accessible name (aria-label) without changing the visible tooltip text */
    ariaLabel = input<string>('');
    shape = input<'round' | 'square'>('round');

    accessibleName = computed(() => this.ariaLabel() || this.tooltip());

    /** Routing */
    routerTo = input<string | any[]>(null);
    queryParams = input<{ [key: string]: any }>({});
    href = input<string>(null);

    /** Icon props */
    icon = input<IconType>();
    iconFill = input<string>('var(--primary-grey-1000)');
    rotate = input<string | number>(0);

    readonly clicked = output<void>();

    private clickCooldown = false;

    buttonClassList = computed(() => {
        return [
            'btn-icon',
            'btn-icon--' + this.type(),
            'btn-icon--' + this.shape(),
            this.loading() ? 'btn-icon--loading' : false,
            'btn-icon--' + this.size(),
        ].filter(Boolean);
    });

    private static readonly ICON_SIZE_MAP = {
        large: 24,
        medium: 20,
        small: 16,
    };

    iconSizeMapped = computed(() => ButtonIcon.ICON_SIZE_MAP[this.size()] ?? 24);

    iconFillColor = computed(() => {
        if (this.disabled()) {
            return 'var(--primary-grey-500)';
        }

        if (this.type() === 'filled' || this.type() === 'filled-primary') {
            return 'var(--palette-white)';
        }

        return this.iconFill();
    });

    handleClick(event: Event): void {
        if (this.disabled() || this.loading() || this.clickCooldown) {
            return;
        }

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.clickCooldown = true;

        this.clicked.emit();

        // Click cooldown is used in place of throttle to prevent accidental double clicks
        setTimeout(() => {
            this.clickCooldown = false;
        }, 300);
    }
}
